import { ArrowUp, Mic, Sparkles, Utensils, ShoppingBag, X } from "lucide-react";
import { Reveal } from "./motion";
import { PhoneFrame } from "./devices";

function VoicePhone() {
  const drafts = [
    { icon: Utensils, name: "Lunch", cat: "Food", amt: "~€12", est: true },
    { icon: ShoppingBag, name: "Aldi", cat: "Groceries", amt: "€60", est: false },
  ];
  return (
    <PhoneFrame className="w-[264px]">
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center justify-between pb-3">
          <span className="text-[15px] font-extrabold text-foreground">Say it, don't type it</span>
          <X className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex flex-col items-center gap-3 py-4">
          <div className="relative grid h-24 w-24 place-items-center">
            <span className="absolute inset-0 rounded-full bg-primary/10" />
            <span className="absolute inset-2 rounded-full bg-primary/15 animate-ping" />
            <span className="relative grid h-16 w-16 place-items-center rounded-full bg-primary">
              <Mic className="h-6 w-6 text-primary-foreground" />
            </span>
          </div>
          <span className="text-[15px] font-bold text-primary-text">Listening…</span>
        </div>
        <div className="flex items-center gap-1.5 px-1 pb-2">
          <Sparkles className="h-3 w-3 text-primary-text" />
          <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-primary-text">Heard you say — edit to fix</span>
        </div>
        <div className="space-y-2">
          {drafts.map((d) => (
            <div key={d.name} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10">
                <d.icon className="h-4 w-4 text-primary-text" />
              </span>
              <div className="flex-1">
                <div className="text-sm font-extrabold text-foreground">{d.name}</div>
                <div className="text-[11px] font-medium text-muted-foreground">{d.cat}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm font-bold text-foreground">{d.amt}</div>
                {d.est && <div className="text-[9px] font-bold text-primary-text">estimate</div>}
              </div>
            </div>
          ))}
        </div>
        <button className="mt-4 rounded-full bg-primary py-3 text-sm font-bold text-primary-foreground">Add 2 transactions</button>
      </div>
    </PhoneFrame>
  );
}

function CoachPhone() {
  return (
    <PhoneFrame className="w-[264px]">
      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-border p-4">
          <span className="flex items-center gap-2">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-primary"><Sparkles className="h-3.5 w-3.5 text-primary-foreground" /></span>
            <span className="text-[15px] font-extrabold text-foreground">Money coach</span>
          </span>
          <X className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 space-y-3 p-4">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10"><Sparkles className="h-3.5 w-3.5 text-primary-text" /></span>
            <p className="max-w-[180px] rounded-2xl bg-muted/60 p-2.5 text-[12.5px] leading-snug text-foreground">
              Hi! I'm your money coach. Ask me anything about your budgets, spending, or how to reach your goals faster.
            </p>
          </div>
          <div className="flex justify-end">
            <p className="rounded-2xl bg-primary p-2.5 text-[12.5px] font-medium leading-snug text-primary-foreground">How's my food budget this month?</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10"><Sparkles className="h-3.5 w-3.5 text-primary-text" /></span>
            <p className="max-w-[180px] rounded-2xl bg-muted/60 p-2.5 text-[12.5px] leading-snug text-foreground">
              You're at €320 of €400 — about 80%. €80 left over 9 days, so about €8.90 a day.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 border-t border-border p-3">
          <span className="flex-1 rounded-full bg-muted/60 px-4 py-2.5 text-[12.5px] text-muted-foreground">Ask about your finances…</span>
          <span className="grid h-9 w-9 place-items-center rounded-full bg-primary"><ArrowUp className="h-4 w-4 text-primary-foreground" /></span>
        </div>
      </div>
    </PhoneFrame>
  );
}

export function AISection() {
  return (
    <section id="ai" className="bg-primary/[0.04]">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-bold uppercase tracking-widest text-primary-text">Meet the AI</span>
            <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-foreground sm:text-[2.6rem]">Say it. It sorts it. It coaches you.</h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Tell BudgeAI what you spent in plain words — rough numbers are fine. It parses a draft you
              confirm, sorts it into a category, and a built-in coach answers questions about your money.
            </p>
          </div>
        </Reveal>
        <div className="mt-14 flex flex-col items-start justify-center gap-14 sm:flex-row">
          <Reveal delay={0.1} y={32}>
            <div className="flex flex-col items-center gap-5">
              <VoicePhone />
              <div className="max-w-[260px] text-center">
                <div className="text-[17px] font-bold text-foreground">Voice entry</div>
                <p className="mt-1 text-[13.5px] leading-relaxed text-muted-foreground">Say what you spent — “about 12 on lunch”. A draft comes back to confirm.</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2} y={32}>
            <div className="flex flex-col items-center gap-5">
              <CoachPhone />
              <div className="max-w-[260px] text-center">
                <div className="text-[17px] font-bold text-foreground">Money coach</div>
                <p className="mt-1 text-[13.5px] leading-relaxed text-muted-foreground">Ask anything about your budgets, spending or goals — in plain language.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
